import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Percent, ArrowRight, Check, X } from 'lucide-react';
import Input from '../../Input';
import Button from '../../Button';
import './Discount.css';

/**
 * Discount Code Component
 *
 * Three visual states:
 * - Default: action input for entering a discount code
 * - Submitted (success): read-only display with check icon + remove (X) button
 * - Error: editable input pre-filled with the invalid code + red error message
 *
 * Figma parent node: 3972:2861
 * Default mode: 3972:2860
 * Submitted mode: 3972:2862
 */
export default function Discount({
  label = 'Discount code',
  code = '',
  submitted = false,
  error = false,
  discountLabel = '',
  errorLabel = '',
  onSubmit,
  onRemove,
  className = '',
}) {
  const [inputValue, setInputValue] = useState(error ? code : '');

  const handleAction = (val) => {
    const trimmed = (val || inputValue).trim();
    if (trimmed) {
      onSubmit?.(trimmed);
      if (!error) setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAction();
    }
  };

  if (submitted && code && !error) {
    return (
      <div className={`sc-discount${className ? ` ${className}` : ''}`}>
        <div className="sc-discount__submitted">
          <div className="sc-discount__input-display">
            <div className="sc-discount__content">
              <div className="sc-discount__icon">
                <Percent size={20} />
              </div>
              <div className="sc-discount__label-value">
                <span className="sc-discount__label typo-label">{label}</span>
                <span className="sc-discount__value typo-input">{code}</span>
              </div>
              <div className="sc-discount__actions">
                <div className="sc-discount__success-icon">
                  <Check size={20} />
                </div>
                <Button
                  size="tertiary"
                  variant="text-danger"
                  iconOnly
                  iconLeft={<X size={20} />}
                  onClick={onRemove}
                  aria-label="Remove discount"
                />
              </div>
            </div>
          </div>
          {discountLabel && (
            <p className="sc-discount__message typo-h5">{discountLabel}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`sc-discount${className ? ` ${className}` : ''}`}>
      <Input
        type="action"
        label={label}
        icon={<Percent size={20} />}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onAction={handleAction}
        onKeyDown={handleKeyDown}
        actionIcon={<ArrowRight size={20} />}
      />
      {error && errorLabel && (
        <p className="sc-discount__message sc-discount__message--error typo-h5">{errorLabel}</p>
      )}
    </div>
  );
}

Discount.propTypes = {
  label: PropTypes.string,
  code: PropTypes.string,
  submitted: PropTypes.bool,
  error: PropTypes.bool,
  discountLabel: PropTypes.string,
  errorLabel: PropTypes.string,
  onSubmit: PropTypes.func,
  onRemove: PropTypes.func,
  className: PropTypes.string,
};
